'use client';

/**
 * In-chat Thinking timeline — Turn 18 anatomy ("Reasoning & thinking — the
 * component & its states", superseding the 4a gutter/rail layout):
 *
 *   The header is ALWAYS a bare inline row — breathing orb + a short living
 *   label — and only the EVIDENCE ever boxes in (the soft #FBF3E8 tinted
 *   block of steps). When the answer lands it settles to a quiet rested row
 *   ("Thought for Ns · N sources") that doesn't compete with the result;
 *   clicking the rested row reopens the trace.
 *
 * Working header: 15px breathing orb + fixed-vocabulary status label
 * (thinkingStatusLabel — phase-specific, never free-form) + three blinking
 * #E36001 dots, no chevron. Rested row: static orb + "Thought for {N}s"
 * + optional "· {N} sources" + 14px chevron. Mobile scales down one notch
 * (18c: 14px orb / 13px label / 11.5px sources) via sm: variants.
 */

import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatThoughtLabel, thinkingStatusLabel } from '@/lib/thinking-groups';
import {
  OrbAvatar,
  ORB_VARIANT,
} from '@/components/assistant/primitives/orb-avatar';
import type { ThinkingModel } from './thinking-steps';
import { StepRow } from './thinking-steps';

/** Reveal cadence — one thinking step slides in per interval (ms). */
const STEP_REVEAL_MS = 750;

/** The 15px (14px below sm) Sara orb both header rows carry — the shared
 *  OrbAvatar primitive (owner decision: one component, one place to
 *  restyle). `breathing` adds the mock's 2.4s scale/opacity loop for the
 *  working state; the rested row holds it static. */
function HeaderOrb({
  breathing = false,
}: {
  breathing?: boolean;
}): React.ReactElement {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex sm:hidden',
        breathing && 'motion-safe:animate-thinking-breathe'
      )}
    >
      <OrbAvatar size={14} variant={ORB_VARIANT.VOICE} />
    </span>
  );
}

function HeaderOrbDesktop({
  breathing = false,
}: {
  breathing?: boolean;
}): React.ReactElement {
  return (
    <span
      aria-hidden
      className={cn(
        'hidden sm:inline-flex',
        breathing && 'motion-safe:animate-thinking-breathe'
      )}
    >
      <OrbAvatar size={15} variant={ORB_VARIANT.VOICE} />
    </span>
  );
}

/** The three 5px blinking #E36001 dots of the working row (mock's `.dots`,
 *  1.4s blink staggered .2s). Reduced motion: steady dots. */
function BlinkDots(): React.ReactElement {
  return (
    <span aria-hidden className="inline-flex items-center">
      {[0, 0.2, 0.4].map((delay) => (
        <span
          key={delay}
          className="mx-[1.5px] inline-block h-[5px] w-[5px] rounded-full bg-[#E36001] motion-safe:animate-thinking-blink"
          style={{ animationDelay: `${delay}s` }}
        />
      ))}
    </span>
  );
}

/** [Turn 18] The rested row — the done state's whole presence, and the
 *  reopened trace's header. "Thought for {N}s" + optional "· {N} sources"
 *  + chevron (up while the trace is open). */
function RestedRow({
  workedSeconds,
  sourceCount,
  expanded,
  onToggle,
}: {
  workedSeconds: number | null;
  sourceCount: number;
  expanded: boolean;
  onToggle: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="inline-flex items-center gap-[9px] self-start font-['Work_Sans'] sm:gap-[10px]"
    >
      <HeaderOrb />
      <HeaderOrbDesktop />
      <span className="text-[13px] font-semibold text-[#8a7c68] transition-colors hover:text-[#31364e] sm:text-[14px]">
        {formatThoughtLabel(workedSeconds)}
      </span>
      {sourceCount > 0 && (
        <span className="text-[11.5px] text-[#b7ab99] sm:text-[12.5px]">
          · {sourceCount} source{sourceCount === 1 ? '' : 's'}
        </span>
      )}
      <ChevronDown
        className={cn(
          'h-[13px] w-[13px] text-[#b7ab99] sm:h-[14px] sm:w-[14px]',
          expanded && 'rotate-180'
        )}
      />
    </button>
  );
}

export function ThinkingTimeline({
  model,
  inProgress,
  workedSeconds,
  liveTool,
  onToggle,
}: {
  model: ThinkingModel;
  inProgress: boolean;
  workedSeconds: number | null;
  /** The running tool's name while the turn is live (turn_state.tool) —
   *  keys the fixed-vocabulary status label; null = plain "Thinking". */
  liveTool: string | null;
  onToggle: () => void;
}): React.ReactElement {
  // Reveal steps one at a time on the live turn so they cascade in rather than
  // popping together. A completed turn the user re-expands shows them all at
  // once. As commentary streams in and new steps arrive, the count keeps
  // ticking up at STEP_REVEAL_MS.
  const total = model.steps.length;
  const [visible, setVisible] = useState(() =>
    inProgress ? Math.min(1, total) : total
  );

  useEffect(() => {
    if (!inProgress) {
      setVisible(total);
      return;
    }
    if (visible >= total) return;
    const id = setTimeout(
      () => setVisible((v) => Math.min(v + 1, total)),
      STEP_REVEAL_MS
    );
    return () => clearTimeout(id);
  }, [inProgress, visible, total]);

  const shown = model.steps.slice(0, visible);

  return (
    <div className="mt-1 flex flex-col gap-[10px] font-['Work_Sans']">
      {inProgress ? (
        // Working header — breathing orb + living label + blink dots. No
        // chevron (18a/18b working rows carry none); still tappable to
        // manually tuck the trace away.
        <button
          type="button"
          onClick={onToggle}
          aria-expanded
          className="inline-flex items-center gap-[9px] self-start sm:gap-[10px]"
        >
          <HeaderOrb breathing />
          <HeaderOrbDesktop breathing />
          <span className="text-[13px] font-semibold text-[#8a7c68] sm:text-[14px]">
            {thinkingStatusLabel(liveTool)}
          </span>
          <BlinkDots />
        </button>
      ) : (
        <RestedRow
          workedSeconds={workedSeconds}
          sourceCount={model.faviconStrip.length}
          expanded
          onToggle={onToggle}
        />
      )}
      {shown.length > 0 && (
        // The evidence block — the ONLY boxed part of the component.
        <div className="flex flex-col gap-[15px] rounded-[12px] bg-[#FBF3E8] px-[15px] py-[13px]">
          {shown.map((s) => (
            <StepReveal key={s.id}>
              {/* The newest step is the in-flight one while working — the
                  mock dims it to 62% ("Pulling a guided meditation…"). */}
              <StepRow
                step={s}
                dimmed={
                  inProgress && s.id === model.steps[model.steps.length - 1]?.id
                }
              />
            </StepReveal>
          ))}
        </div>
      )}
    </div>
  );
}

/** One step's entrance — a gentle slide-down + fade as it's revealed. */
function StepReveal({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div
      className={cn(
        'transition-all duration-300 ease-out',
        shown ? 'translate-y-0 opacity-100' : '-translate-y-1.5 opacity-0'
      )}
    >
      {children}
    </div>
  );
}

/** Collapsed = the rested row alone (Turn 18 dropped the old favicon strip —
 *  the "· N sources" count carries that information now). A manually-
 *  collapsed LIVE turn shows the working row instead, so the label stays
 *  honest while Sara is still at it. */
export function ThinkingCollapsed({
  model,
  inProgress,
  workedSeconds,
  liveTool,
  onToggle,
}: {
  model: ThinkingModel;
  inProgress: boolean;
  workedSeconds: number | null;
  liveTool: string | null;
  onToggle: () => void;
}): React.ReactElement {
  if (inProgress) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={false}
        className="mt-1 inline-flex items-center gap-[9px] self-start font-['Work_Sans'] sm:gap-[10px]"
      >
        <HeaderOrb breathing />
        <HeaderOrbDesktop breathing />
        <span className="text-[13px] font-semibold text-[#8a7c68] sm:text-[14px]">
          {thinkingStatusLabel(liveTool)}
        </span>
        <BlinkDots />
      </button>
    );
  }
  return (
    <div className="mt-1 font-['Work_Sans']">
      <RestedRow
        workedSeconds={workedSeconds}
        sourceCount={model.faviconStrip.length}
        expanded={false}
        onToggle={onToggle}
      />
    </div>
  );
}
